import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { TenantSummary } from '../api/types';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function TenantDialog({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: TenantSummary | undefined;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editing = tenant !== undefined;

  const [displayName, setDisplayName] = useState(tenant?.displayName ?? '');
  const [slug, setSlug] = useState(tenant?.slug ?? '');
  const [id, setId] = useState(tenant?.id ?? '');
  const [status, setStatus] = useState<'active' | 'suspended'>(tenant?.status ?? 'active');
  const [slugTouched, setSlugTouched] = useState(editing);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleDisplayNameChange = (value: string): void => {
    setDisplayName(value);
    if (!slugTouched && !editing) {
      const generated = slugify(value);
      setSlug(generated);
      if (!id || id === slug) {
        setId(generated);
      }
    }
  };

  const save = useMutation<void, { id: string } | void>(
    useCallback(async () => {
      if (editing) {
        return apiFetch<void>(`/api/admin/tenants/${encodeURIComponent(tenant.id)}`, {
          method: 'PUT',
          body: { displayName: displayName.trim(), status },
        });
      }
      return apiFetch<{ id: string }>('/api/admin/tenants', {
        method: 'POST',
        body: {
          id: id.trim() || undefined,
          slug: slug.trim(),
          displayName: displayName.trim(),
          status,
        },
      });
    }, [editing, tenant?.id, displayName, slug, id, status]),
  );

  const submit = async (): Promise<void> => {
    const result = await save.run();
    if (result !== undefined) onSaved();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>
        {editing
          ? t('tenants.editHeading', { name: tenant.displayName })
          : t('tenants.createHeading')}
      </h2>

      <label className="field">
        <span>{t('tenants.displayNameField')}</span>
        <input
          type="text"
          value={displayName}
          required
          placeholder={t('tenants.displayNamePlaceholder')}
          onChange={(e) => handleDisplayNameChange(e.target.value)}
        />
      </label>

      {!editing ? (
        <>
          <label className="field">
            <span>{t('tenants.slugField')}</span>
            <input
              type="text"
              value={slug}
              required
              placeholder="e.g. site-danang-01"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              }}
            />
            <small className="hint">{t('tenants.slugHint')}</small>
          </label>

          <label className="field">
            <span>{t('tenants.idField')}</span>
            <input
              type="text"
              value={id}
              placeholder="e.g. site-danang-01"
              onChange={(e) => setId(e.target.value)}
            />
            <small className="hint">{t('tenants.idHint')}</small>
          </label>
        </>
      ) : (
        <div className="field">
          <span>{t('tenants.column.id')}</span>
          <div className="mono muted" style={{ marginTop: 4 }}>
            {tenant.id}
          </div>
        </div>
      )}

      <label className="field">
        <span>{t('tenants.statusField')}</span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'active' | 'suspended')}
        >
          <option value="active">{t('tenants.status.active')}</option>
          <option value="suspended">{t('tenants.status.suspended')}</option>
        </select>
      </label>

      {save.error ? <ErrorNotice error={save.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          disabled={save.pending || !displayName.trim() || (!editing && !slug.trim())}
          onClick={() => void submit()}
        >
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
